// EasyExile.tools Shared TopBar Component
// This is synced from _shared/TopBar.jsx

import React from 'react';

interface TopBarProps {
  activeTool?: string;
}

const TopBar: React.FC<TopBarProps> = ({ activeTool = 'idolplanner' }) => {
  // Define all tools with their properties
  const tools = [
    { 
      id: 'disenchant',
      name: 'Best Uniques to Disenchant', 
      url: 'https://disenchant.easyexile.tools/',
      badges: [] as Array<{ text: string; color: string }>,
      openInNewTab: false
    },
    { 
      id: 'mapsearch',
      name: 'Better Map Search', 
      url: 'https://mapsearch.easyexile.tools/',
      badges: [] as Array<{ text: string; color: string }>,
      openInNewTab: false
    },
    { 
      id: 'tradeextension',
      name: 'Trade Extension',
      isComposite: true, // Special handling for Chrome/Firefox links
      badges: [] as Array<{ text: string; color: string }>,
      openInNewTab: false
    },
    { 
      id: 'idolplanner',
      name: 'Phrecia Idol Planner', 
      url: 'https://idolplanner.easyexile.tools/',
      badges: [
        { text: 'UPDATED', color: 'green' }
      ],
      openInNewTab: false
    },
  ];

  const badgeStyles: Record<string, string> = {
    green: 'bg-green-800/70 text-green-400',
    blue: 'bg-blue-800/70 text-blue-400',
    gray: 'bg-gray-800/70 text-gray-500'
  };

  return (
    <div className="bg-black/40 border-b-2 border-yellow-400/20 shadow-lg">
      <div className="max-w-screen-2xl mx-auto px-3 sm:px-4 py-3">
        <div className="flex items-center gap-4 sm:gap-8">
          {/* Logo */}
          <div className="flex-shrink-0">
            <span className="relative">
              <span 
                className="text-xl sm:text-2xl font-bold text-yellow-400 tracking-wide font-logo relative" 
                style={{ 
                  textShadow: '0 0 10px rgba(250, 200, 0, 0.5), 0 0 20px rgba(250, 200, 0, 0.3), 2px 2px 4px rgba(0, 0, 0, 0.8)', 
                  filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5))' 
                }}
              >
                EasyExile
              </span>
              <span 
                className="text-xs sm:text-sm font-bold text-yellow-400/70 tracking-wide font-logo" 
                style={{ 
                  textShadow: '0 0 8px rgba(250, 200, 0, 0.4), 1px 1px 3px rgba(0, 0, 0, 0.8)' 
                }}
              >
                .tools
              </span>
            </span>
          </div>

          {/* Separator */}
          <div className="hidden sm:block h-6 w-px bg-gray-600/50"></div>

          {/* Navigation Links */}
          <nav className="flex items-center gap-4 sm:gap-6 text-xs sm:text-sm overflow-x-auto">
            {tools.map(tool => {
              const isActive = tool.id === activeTool;
              
              // Special handling for Trade Extension with Chrome/Firefox links
              if (tool.isComposite) {
                return (
                  <span 
                    key={tool.id}
                    className={`whitespace-nowrap flex items-center gap-1 py-1 ${isActive ? 'text-yellow-400 font-semibold border-b-2 border-yellow-400' : 'text-gray-400'}`}
                  >
                    <a
                      href="https://chromewebstore.google.com/detail/lmmjicanpdjejcoikdmklalcgejobgpe?utm_source=item-share-cb"
                      className="hover:text-yellow-400 transition-colors"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Chrome
                    </a>
                    <span> / </span>
                    <a
                      href="https://addons.mozilla.org/pl/firefox/addon/easyexile-trade-extension/"
                      className="hover:text-yellow-400 transition-colors"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Firefox
                    </a>
                    <span> {tool.name}</span>
                  </span>
                );
              }
              
              if (isActive) {
                return (
                  <span 
                    key={tool.id}
                    className="text-yellow-400 font-semibold whitespace-nowrap border-b-2 border-yellow-400 flex items-center gap-1 py-1"
                  >
                    {tool.name}
                    {tool.badges.map((badge, idx) => (
                      <span 
                        key={idx}
                        className={`text-[10px] px-1 py-0 rounded ${badgeStyles[badge.color]}`}
                      >
                        {badge.text}
                      </span>
                    ))}
                  </span>
                );
              }
              
              return (
                <a
                  key={tool.id}
                  href={tool.url}
                  className="text-gray-400 hover:text-yellow-400 transition-colors whitespace-nowrap flex items-center gap-1 py-1"
                  {...(tool.openInNewTab && { target: '_blank', rel: 'noopener noreferrer' })}
                >
                  {tool.name}
                  {tool.badges.map((badge, idx) => (
                    <span 
                      key={idx}
                      className={`text-[10px] px-1 py-0 rounded ${badgeStyles[badge.color]}`}
                    >
                      {badge.text}
                    </span>
                  ))}
                </a>
              );
            })}
          </nav>
        </div>
      </div>
    </div>
  );
};

export default TopBar;
